import { Check, ExternalLink, KeyRound, Palette, Plus, SlidersHorizontal } from "lucide-react";
import { Link } from "react-router-dom";
import { errorMessage } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { useCreateEmbedKey, useCredits, useEmbedKeys, useLeads } from "../api/queries";
import { InstallSnippets } from "../components/InstallSnippets";
import { OriginsEditor } from "../components/OriginsEditor";
import { Alert, Badge, Button, Card, CopyButton, Spinner, cx, useToast } from "../components/ui";
import { widgetSrc } from "../lib/snippets";

function Step({ n, done, title, description, children }: {
  n: number;
  done?: boolean;
  title: string;
  description?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={cx("step", done && "done")}>
      <div className="step-num">{done ? <Check /> : n}</div>
      <div>
        <div className="spread">
          <h3>{title}</h3>
          {done && <Badge tone="ok">Done</Badge>}
        </div>
        {description && <p className="muted" style={{ marginTop: 2 }}>{description}</p>}
        {children && <div className="step-body">{children}</div>}
      </div>
    </div>
  );
}

export function GetStarted() {
  const orgId = useOrgId();
  const { membership, canManage } = useOrg();
  const keys = useEmbedKeys(orgId);
  const credits = useCredits(orgId, 5);
  const leads = useLeads(orgId, { source: "website" }, 1);
  const create = useCreateEmbedKey(orgId);
  const toast = useToast();

  if (keys.isLoading) return <Spinner />;
  const live = keys.data?.items.find((k) => k.mode === "live" && k.active) ?? keys.data?.items.find((k) => k.active);
  const hasOrigins = !!live?.allowed_origins.length;
  const hasLead = !!leads.data?.items.length;
  const balance = credits.data?.balance ?? 0;

  return (
    <div className="stack-lg">
      <section className="hero">
        <span className="badge grad plain">Welcome to SolarOS</span>
        <h1 style={{ marginTop: 12 }}>Let’s get {membership?.organization_name ?? "you"} quoting from your website.</h1>
        <p>Three steps, about two minutes. Visitors type their address and get an AI roof design with a priced proposal — and you get the lead.</p>
        <div className="hero-stats">
          <div className="hero-stat">
            <b>{balance}</b>
            <span>design credit{balance === 1 ? "" : "s"} ready</span>
          </div>
          <div className="hero-stat">
            <b>{live ? (live.mode === "live" ? "Live" : "Test") : "—"}</b>
            <span>embed key</span>
          </div>
          <div className="hero-stat">
            <b>{live?.allowed_origins.length ?? 0}</b>
            <span>website{live?.allowed_origins.length === 1 ? "" : "s"} allowed</span>
          </div>
        </div>
      </section>

      {!live ? (
        <Card title="Your embed key">
          <div className="spread">
            <p className="muted">You have no active key yet. Create one to put the calculator on your website.</p>
            <Button
              variant="primary"
              icon={<Plus />}
              disabled={!canManage}
              loading={create.isPending}
              onClick={() =>
                create.mutate({ label: "Website", mode: "live", allowed_origins: [] }, { onError: (e) => toast(errorMessage(e), true) })
              }
            >
              Create embed key
            </Button>
          </div>
        </Card>
      ) : (
        <div className="split-wide">
          <Card pad={false}>
            <div className="card-body steps">
              <Step n={1} done={hasOrigins} title="Add your website domain" description="The calculator only answers on the websites you list here.">
                <OriginsEditor orgId={orgId} embedKey={live} canManage={canManage} />
              </Step>
              <div className="divider" />
              <Step n={2} done={hasLead} title="Put the calculator on your site" description="Use the WordPress plugin or paste the iframe snippet.">
                <InstallSnippets embedKey={live.key} />
              </Step>
              <div className="divider" />
              <Step n={3} done={hasLead} title="Get your first quote" description="Try it yourself: your first design is free. New leads show up under Designs & leads.">
                <div className="row">
                  <a className="btn btn-primary" href={widgetSrc(live.key)} target="_blank" rel="noreferrer">
                    <ExternalLink /> Try your calculator
                  </a>
                  <Link className="btn" to="/designs">
                    See leads
                  </Link>
                </div>
              </Step>
            </div>
          </Card>
          <div className="stack">
            <Card title="Your live embed key" description="Publishable: safe to put in your website’s HTML.">
              <div className="stack" style={{ gap: 10 }}>
                <div className="keybox">
                  <KeyRound size={16} color="var(--primary)" />
                  <code>{live.key}</code>
                  <CopyButton text={live.key} />
                </div>
                <div className="row small muted">
                  <Badge tone={live.mode === "live" ? "ok" : "warn"}>{live.mode}</Badge>
                  <span>{live.label || "Website"}</span>
                  <Link to="/widget" style={{ marginLeft: "auto" }}>
                    Manage keys
                  </Link>
                </div>
              </div>
            </Card>
            <Card title="Make it yours" description="Optional — the defaults work out of the box.">
              <div className="stack" style={{ gap: 8 }}>
                <Link className="btn btn-block" to="/branding" style={{ justifyContent: "flex-start" }}>
                  <Palette /> Logo, colours and texts
                </Link>
                <Link className="btn btn-block" to="/rules" style={{ justifyContent: "flex-start" }}>
                  <SlidersHorizontal /> Pricing rules, modules, service area
                </Link>
              </div>
            </Card>
            {balance < 1 && (
              <Alert tone="warn" title="No design credits left." action={<Link className="btn btn-sm" to="/credits">Buy credits</Link>}>
                New website quotes need one credit each.
              </Alert>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
